import dayjs from "dayjs";
import type { ScreenerRequestError } from "./hooks/useScreenerRuns";
import type {
  NewMatchEvent,
  SavedScreenSummary,
  ScreenAlertStatus,
  ScreenAlertsResponse,
} from "./types";

// Tracks ALERT_MAX_MATCHES (@insight/shared/screener/alertBaseline).
const ALERT_MATCH_LIMIT = 1000;

export const ALERT_FREQUENCY = "At most once a day";

const limitText = (limit: number = ALERT_MATCH_LIMIT) =>
  `over ${limit.toLocaleString("en-GB")}`;

/** Alerts were switched on for the screen (whatever their progress). */
export const alertsOn = (status: ScreenAlertStatus | undefined): boolean =>
  status !== undefined && status !== "off";

/**
 * The truthful state of a screen's alerts, in one line. A Pro caller reading
 * `paused_plan` has just come back to Pro: the next daily check resumes them.
 */
export const alertStatusLine = (
  status: ScreenAlertStatus,
  isPro: boolean,
): string => {
  if (status === "active") return "Alerts on";
  if (status === "baseline_pending")
    return "We'll start once today's data is ready";
  if (status === "too_broad")
    return `Too many matches to alert on (${limitText()})`;
  if (status === "invalid_filters") return "Needs updating";
  if (status === "paused_plan" && isPro)
    return "Alerts resume after the next daily check";
  if (status === "paused_plan") return "Alerts paused";
  return "Alerts off";
};

/** The toast after alerts were switched on. */
export const alertsOnToast = (result: ScreenAlertsResponse): string => {
  if (result.alertStatus === "baseline_pending")
    return "Alerts on. We'll start once today's data is ready.";
  return `Alerts on. ${ALERT_FREQUENCY}.`;
};

/** After "Save changes": a filter edit restarts alerts from today's matches. */
export const savedChangesToast = (status: ScreenAlertStatus): string => {
  if (status === "active")
    return "Changes saved. Alerts start again from today's matches.";
  if (status === "baseline_pending")
    return "Changes saved. Alerts restart once today's data is ready.";
  if (status === "too_broad")
    return `Changes saved. Too many matches to alert on (${limitText()}).`;
  return "Changes saved.";
};

/** Why switching alerts on or off failed, from the API's refusal. */
export const alertsErrorCopy = (error: ScreenerRequestError | null): string => {
  const code = error?.body?.error;
  if (code === "SCREEN_TOO_BROAD")
    return `Too many matches to alert on (${limitText(error?.body?.matchLimit)}). Narrow the screen to get alerts.`;
  if (code === "SCREEN_NEEDS_UPDATE")
    return "This screen needs updating before it can send alerts.";
  if (code === "RATE_LIMITED")
    return "That's a lot of changes at once. Try again in a minute.";
  return "We couldn't change this screen's alerts. Try again.";
};

/**
 * The landing row's second line. No timestamps: refresh timing is internal.
 * The match count is the screen's total today, as opening it shows, for any
 * screen; null when there is nothing true to say (no total stored yet).
 */
export const savedScreenSubtitle = (
  screen: SavedScreenSummary,
  isPro: boolean,
): string | null => {
  if (screen.invalid || screen.alertStatus === "invalid_filters")
    return "Needs updating";
  if (screen.alertStatus === "paused_plan")
    return alertStatusLine("paused_plan", isPro);
  if (screen.alertStatus === "too_broad") return "Too many matches to alert on";
  if (screen.alertStatus === "baseline_pending")
    return "Alerts start once today's data is ready";
  const count = screen.matchCount;
  const matching =
    count !== null && count > 0 ? `${count.toLocaleString()} match` : null;
  // Follows the names pinned above the results; the dot is the inbox's.
  if (screen.newCount > 0) {
    const fresh = `${screen.newCount.toLocaleString()} new`;
    return matching ? `${fresh} · ${matching}` : fresh;
  }
  if (count === 0) return "No matches yet";
  return matching;
};

/** The screen's filters changed after the alert: its statuses no longer apply. */
export const editedSinceLine = (date: string): string =>
  `Matched on ${dayjs(date).format("D MMM")} · you've changed this screen since`;

/**
 * The NEW section's lines: "1 match from your alert on Fri 9 Oct" (or "4
 * matches from your alerts since Mon 5 Oct"), and how many no longer match,
 * which sits on a line of its own (Ben, 2026-10-19). `parts` are the line's
 * pieces, which touch puts on lines of their own, clear of the button above
 * (Ben, 2026-10-21). `days` are the shown alert days, newest first.
 */
export const newMatchesLine = (
  days: readonly NewMatchEvent[],
  count: number,
  noLongerMatchCount: number,
): { line: string; parts: string[]; noLongerMatch: string | null } => {
  if (days.length === 1 && days[0].editedSince) {
    const line = editedSinceLine(days[0].tradingDate);
    return { line, parts: [line], noLongerMatch: null };
  }
  const names = `${count.toLocaleString()} ${count === 1 ? "match" : "matches"}`;
  const oldest = dayjs(days[days.length - 1]?.tradingDate).format("ddd D MMM");
  const source =
    days.length === 1
      ? `from your alert on ${oldest}`
      : `from your alerts since ${oldest}`;
  const parts = [names, source];
  const line = parts.join(" ");
  if (noLongerMatchCount === 0) return { line, parts, noLongerMatch: null };
  const verb = noLongerMatchCount === 1 ? "matches" : "match";
  return {
    line,
    parts,
    noLongerMatch: `${noLongerMatchCount.toLocaleString()} no longer ${verb}`,
  };
};

/** A YYYY-MM-DD calendar date, as the alert links carry. */
export const isAlertDate = (value: string | null): value is string =>
  value !== null &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  dayjs(value).format("YYYY-MM-DD") === value;
